import React, { useMemo } from "react";
import {
  Input,
  InputNumber,
  Select,
  Button,
  Upload,
  Table,
  Tag,
  Space,
  Typography,
  Empty,
  notification,
  Alert,
  Radio,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  UploadOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

// Produit cartesien des axes : [{name:"Taille",values:["S","M"]},{name:"Couleur",values:["Rouge"]}]
// -> [{Taille:"S",Couleur:"Rouge"},{Taille:"M",Couleur:"Rouge"}]
export const cartesianCombinations = (axes) => {
  const validAxes = (axes || []).filter(
    (a) =>
      String(a.name || "").trim() &&
      Array.isArray(a.values) &&
      a.values.length > 0
  );
  if (validAxes.length === 0) return [];
  return validAxes.reduce(
    (acc, axis) => {
      const next = [];
      acc.forEach((combo) => {
        axis.values.forEach((value) => {
          next.push({ ...combo, [String(axis.name).trim()]: value });
        });
      });
      return next;
    },
    [{}]
  );
};

export const comboKey = (combo) =>
  Object.keys(combo)
    .sort()
    .map((k) => `${k}=${combo[k]}`)
    .join("|");

export const comboLabel = (combo) =>
  Object.values(combo)
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(" / ");

// Construit le payload `variant_attributes` a partir des axes valides.
export const buildVariantAttributes = (axes) =>
  (axes || [])
    .filter((a) => String(a.name || "").trim() && a.values?.length)
    .map((a) => ({ name: String(a.name).trim(), values: a.values }));

/**
 * Gestionnaire de declinaisons (controle) : editeur d'axes + tableau des variantes.
 * Concu pour etre integre DANS le formulaire produit standard (onglet "Declinaisons")
 * quand le mode variantes est active via le toggle.
 *
 * Props :
 *  - axes / setAxes        : etat des axes [{ name, values: [] }]
 *  - variants / setVariants: etat des declinaisons generees
 *  - getDefaults()         : retourne { purchase_price, sales_price, tax_id } pour
 *                            pre-remplir les nouvelles lignes (valeurs partagees du form)
 */
const VariantManager = ({
  axes = [],
  setAxes,
  variants = [],
  setVariants,
  getDefaults,
  existingProduct = null,
  existingVariantKey = null,
  onExistingVariantChange,
}) => {
  // ----- Gestion des axes -----
  const updateAxisName = (index, name) => {
    setAxes((prev) => prev.map((a, i) => (i === index ? { ...a, name } : a)));
  };
  const updateAxisValues = (index, values) => {
    setAxes((prev) => prev.map((a, i) => (i === index ? { ...a, values } : a)));
  };
  const addAxis = () => {
    if (axes.length >= 3) {
      notification.warning({
        message: "3 axes maximum (ex: Taille, Couleur, Matière)",
      });
      return;
    }
    setAxes((prev) => [...prev, { name: "", values: [] }]);
  };
  const removeAxis = (index) => {
    setAxes((prev) => prev.filter((_, i) => i !== index));
  };

  const previewCount = useMemo(() => cartesianCombinations(axes).length, [axes]);

  // ----- Generation des combinaisons -----
  const generateCombinations = () => {
    const combos = cartesianCombinations(axes);
    if (combos.length === 0) {
      notification.warning({
        message: "Définissez au moins un axe avec des valeurs",
        description: "Ex: axe 'Taille' avec les valeurs S, M, L.",
      });
      return;
    }
    if (combos.length > 200) {
      notification.error({
        message: "Trop de combinaisons",
        description: `${combos.length} déclinaisons générées. Limitez le nombre de valeurs (max 200).`,
      });
      return;
    }
    const defaults =
      (typeof getDefaults === "function" ? getDefaults() : null) || {};
    if (
      existingVariantKey &&
      !combos.some((combo) => comboKey(combo) === existingVariantKey)
    ) {
      onExistingVariantChange?.(null);
    }
    setVariants((prev) => {
      const existingByKey = (prev || []).reduce((map, row) => {
        map[row.key] = row;
        return map;
      }, {});
      return combos.map((combo) => {
        const key = comboKey(combo);
        const existing = existingByKey[key];
        if (existing) {
          return { ...existing, variant_values: combo, label: comboLabel(combo) };
        }
        return {
          key,
          variant_values: combo,
          label: comboLabel(combo),
          item_code: "",
          barcode_value: "",
          purchase_price: defaults.purchase_price ?? 0,
          sales_price: defaults.sales_price ?? 0,
          opening_stock: 0,
          stock_quantitiy_alert: defaults.stock_quantitiy_alert ?? 0,
          tax_id: defaults.tax_id ?? null,
          imageFile: null,
        };
      });
    });
  };

  const updateVariant = (key, field, value) => {
    setVariants((prev) =>
      prev.map((row) => (row.key === key ? { ...row, [field]: value } : row))
    );
  };
  const removeVariant = (key) => {
    if (existingProduct && key === existingVariantKey) {
      notification.warning({
        message: "Cette déclinaison représente le produit actuel",
        description:
          "Choisissez d'abord une autre déclinaison pour le produit existant.",
      });
      return;
    }
    setVariants((prev) => prev.filter((row) => row.key !== key));
  };

  const variantColumns = [
    ...(existingProduct
      ? [
          {
            title: "Produit actuel",
            dataIndex: "existing_product",
            fixed: "left",
            width: 145,
            render: (_, row) => (
              <Radio
                checked={row.key === existingVariantKey}
                onChange={() => onExistingVariantChange?.(row.key)}
              >
                Conserver ici
              </Radio>
            ),
          },
        ]
      : []),
    {
      title: "Déclinaison",
      dataIndex: "label",
      fixed: "left",
      width: 150,
      render: (label) => <Tag color="blue">{label}</Tag>,
    },
    {
      title: "SKU (auto si vide)",
      dataIndex: "item_code",
      width: 150,
      render: (value, row) => (
        <Input
          size="small"
          placeholder="Auto"
          value={
            row.key === existingVariantKey && existingProduct
              ? existingProduct.item_code || ""
              : value
          }
          disabled={row.key === existingVariantKey && Boolean(existingProduct)}
          onChange={(e) => updateVariant(row.key, "item_code", e.target.value)}
        />
      ),
    },
    {
      title: "Code-barres",
      dataIndex: "barcode_value",
      width: 150,
      render: (value, row) => (
        <Input
          size="small"
          placeholder="= SKU si vide"
          value={
            row.key === existingVariantKey && existingProduct
              ? existingProduct.barcode_value || existingProduct.item_code || ""
              : value
          }
          disabled={row.key === existingVariantKey && Boolean(existingProduct)}
          onChange={(e) =>
            updateVariant(row.key, "barcode_value", e.target.value)
          }
        />
      ),
    },
    {
      title: "Prix achat",
      dataIndex: "purchase_price",
      width: 110,
      render: (value, row) => (
        <InputNumber
          size="small"
          min={0}
          style={{ width: "100%" }}
          value={value}
          onChange={(v) => updateVariant(row.key, "purchase_price", v)}
        />
      ),
    },
    {
      title: "Prix vente",
      dataIndex: "sales_price",
      width: 110,
      render: (value, row) => (
        <InputNumber
          size="small"
          min={0}
          style={{ width: "100%" }}
          value={value}
          onChange={(v) => updateVariant(row.key, "sales_price", v)}
        />
      ),
    },
    {
      title: "Stock initial",
      dataIndex: "opening_stock",
      width: 110,
      render: (value, row) => (
        <InputNumber
          size="small"
          min={0}
          style={{ width: "100%" }}
          value={
            row.key === existingVariantKey && existingProduct
              ? existingProduct.current_stock ?? value
              : value
          }
          disabled={row.key === existingVariantKey && Boolean(existingProduct)}
          onChange={(v) => updateVariant(row.key, "opening_stock", v)}
        />
      ),
    },
    {
      title: "Seuil alerte",
      dataIndex: "stock_quantitiy_alert",
      width: 100,
      render: (value, row) => (
        <InputNumber
          size="small"
          min={0}
          style={{ width: "100%" }}
          value={value}
          onChange={(v) => updateVariant(row.key, "stock_quantitiy_alert", v)}
        />
      ),
    },
    {
      title: "Image",
      dataIndex: "imageFile",
      width: 90,
      render: (value, row) => (
        <Upload
          disabled={row.key === existingVariantKey && Boolean(existingProduct)}
          beforeUpload={(file) => {
            updateVariant(row.key, "imageFile", file);
            return false;
          }}
          maxCount={1}
          showUploadList={false}
          accept="image/*"
        >
          <Button
            size="small"
            icon={<UploadOutlined />}
            disabled={row.key === existingVariantKey && Boolean(existingProduct)}
          >
            {row.key === existingVariantKey && existingProduct
              ? "Conservée"
              : value
              ? "1"
              : ""}
          </Button>
        </Upload>
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      fixed: "right",
      width: 50,
      render: (_, row) => (
        <Button
          size="small"
          danger
          type="text"
          icon={<DeleteOutlined />}
          disabled={row.key === existingVariantKey && Boolean(existingProduct)}
          onClick={() => removeVariant(row.key)}
        />
      ),
    },
  ];

  return (
    <div className="variant-manager">
      <div className="form-section">
        <div className="form-section-header">
          <div>
            <h3 className="section-title">Axes de déclinaison</h3>
            <p className="section-subtitle">
              Définissez les axes (ex: Taille, Couleur) puis générez
              automatiquement toutes les combinaisons. Le stock est suivi
              séparément pour chaque déclinaison.
            </p>
          </div>
        </div>

        <Space direction="vertical" style={{ width: "100%" }} size={12}>
          {axes.map((axis, index) => (
            <div key={index} className="variant-axis-row">
              <div className="variant-axis-name">
                <label className="variant-axis-label">Axe</label>
                <Input
                  placeholder="Ex: Taille, Couleur"
                  value={axis.name}
                  onChange={(e) => updateAxisName(index, e.target.value)}
                />
              </div>
              <div className="variant-axis-values">
                <label className="variant-axis-label">Valeurs</label>
                <Select
                  mode="tags"
                  style={{ width: "100%" }}
                  placeholder="Saisir une valeur puis Entrée (ex: S, M, L)"
                  value={axis.values}
                  onChange={(vals) => updateAxisValues(index, vals)}
                  tokenSeparators={[",", ";"]}
                />
              </div>
              {axes.length > 1 && (
                <Button
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => removeAxis(index)}
                  className="variant-axis-remove"
                />
              )}
            </div>
          ))}
          <Space wrap>
            <Button icon={<PlusOutlined />} onClick={addAxis}>
              Ajouter un axe
            </Button>
            <Button
              type="primary"
              ghost
              icon={<ThunderboltOutlined />}
              onClick={generateCombinations}
            >
              Générer les combinaisons{previewCount ? ` (${previewCount})` : ""}
            </Button>
          </Space>
        </Space>
      </div>

      <div className="form-section">
        <div className="form-section-header">
          <div>
            <h3 className="section-title">
              Déclinaisons{variants.length ? ` (${variants.length})` : ""}
            </h3>
            <p className="section-subtitle">
              Renseignez le prix et le stock de chaque déclinaison. Le SKU et le
              code-barres sont générés automatiquement si laissés vides.
            </p>
          </div>
        </div>

        {existingProduct && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Choisissez la déclinaison correspondant au produit actuel"
            description={`Le produit « ${existingProduct.name} » conservera son identifiant, son code-barres, son stock actuel (${existingProduct.current_stock ?? 0}) et tout son historique. Les autres déclinaisons seront créées séparément.`}
          />
        )}

        {variants.length ? (
          <Table
            size="small"
            rowKey="key"
            dataSource={variants}
            columns={variantColumns}
            pagination={false}
            scroll={{ x: existingProduct ? 1095 : 950 }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary">
                Aucune déclinaison. Définissez les axes puis cliquez sur «
                Générer les combinaisons ».
              </Text>
            }
          />
        )}
      </div>
    </div>
  );
};

export default VariantManager;
